import React, { useMemo, useState } from 'react'
import { Modal, Col, Row, Button } from 'react-bootstrap'
import type { IFilterDataArray } from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/pro-duotone-svg-icons'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { Segment } from 'models/segment.model'
import { SegmentModal } from '../../segments'
import SelectSegments from './components/SelectSegments'

interface AddSegmentsModalProps {
  show: boolean
  onClose(): void
  onConfirm: (users: Segment[]) => void | Promise<void>
  segmentsAdded?: Segment[]
  filter?: IFilterDataArray[]
  saving?: boolean
}

const prefix = 'configuration.lmsProfiles.segments.add'

const AddSegmentsModal: React.FC<AddSegmentsModalProps> = ({
  show,
  onClose,
  onConfirm,
  segmentsAdded = [],
  filter: initialFilterData,
  saving,
}) => {
  const [disabled, setDisabled] = useState(false)
  const [selectedUsersMap, setSelectedUsersMap] = useState<
    Map<number, Segment>
  >(new Map())

  const selectedUsers = useMemo(
    () => Array.from(selectedUsersMap.values()),
    [selectedUsersMap]
  )

  const tRelative = useTranslationRankmi({
    prefix,
  })

  const filterDataMerge = useMemo(
    () => ({
      filterType: 'simple',
      data: [...(initialFilterData || [])],
    }),
    [initialFilterData]
  )

  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [segment, setSegment] = useState<Segment>()

  const handleOnClose = () => {
    onClose()
    setShowFiltersModal(false)
  }

  const handleOnConfirm = async () => {
    await onConfirm(selectedUsers)
    setShowFiltersModal(false)

    setSelectedUsersMap(new Map())
  }

  const hiddenParentModal = !!showFiltersModal || !!segment

  const valid = selectedUsersMap.size > 0

  return (
    <>
      <Modal
        size="xl"
        show={show}
        onHide={handleOnClose}
        backdrop={!hiddenParentModal}
        style={{
          display: hiddenParentModal ? 'none' : 'block',
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{tRelative(`title`)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="position-relative">
            <Col xs={12}>
              <SelectSegments
                selectedMap={selectedUsersMap}
                segmentsAdded={segmentsAdded}
                onAdd={(users) => {
                  const newSelectedUsers = new Map(selectedUsersMap)
                  users.forEach((user) => {
                    newSelectedUsers.set(user.id, user)
                  })

                  setSelectedUsersMap(newSelectedUsers)
                }}
                onRemove={(users) => {
                  const newSelectedUsers = new Map(selectedUsersMap)
                  users.forEach((user) => {
                    newSelectedUsers.delete(user.id)
                  })
                  setSelectedUsersMap(newSelectedUsers)
                }}
                filterData={filterDataMerge}
                onStartLoading={() => {
                  setDisabled(true)
                }}
                onStopLoading={() => {
                  setDisabled(false)
                }}
                onOpenSegment={(segment) => {
                  setSegment(segment)
                }}
              />
            </Col>
            <Col xs={12}>
              <div className="d-flex justify-content-end mt-2">
                <Button
                  variant="secondary"
                  className="mr-2"
                  onClick={handleOnClose}
                >
                  {tRelative(`buttons.cancel`)}
                </Button>
                <Button
                  disabled={disabled || saving || !valid}
                  onClick={() => {
                    handleOnConfirm()
                  }}
                >
                  {saving && (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                  )}
                  {tRelative(`buttons.add`)}
                </Button>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      <SegmentModal
        open={!!segment}
        onClose={() => {
          setSegment(undefined)
        }}
        segment={segment}
      />
    </>
  )
}

export default AddSegmentsModal
